import type { GameState } from './store';
import { createInitialState } from './store';

/**
 * Persistenza locale (Fase 1). L'interfaccia astratta permette in Fase 2 di
 * sostituire localStorage con salvataggi cloud senza toccare il resto del gioco.
 */

export interface SaveBackend {
  read(key: string): string | null;
  write(key: string, value: string): void;
  delete(key: string): void;
}

export const SAVE_KEY = 'custodi-di-lumen/save/v1';

export function localStorageBackend(): SaveBackend {
  return {
    read: (key) => window.localStorage.getItem(key),
    write: (key, value) => window.localStorage.setItem(key, value),
    delete: (key) => window.localStorage.removeItem(key),
  };
}

/** Backend in memoria per i test e per ambienti senza localStorage. */
export function memoryBackend(): SaveBackend {
  const map = new Map<string, string>();
  return {
    read: (key) => map.get(key) ?? null,
    write: (key, value) => void map.set(key, value),
    delete: (key) => void map.delete(key),
  };
}

export function saveGame(backend: SaveBackend, state: GameState): void {
  // La fase corrente non va persistita: al load si riparte sempre da explore.
  const toPersist: GameState = { ...state, phase: 'explore' };
  backend.write(SAVE_KEY, JSON.stringify(toPersist));
}

export function loadGame(backend: SaveBackend): GameState | null {
  const raw = backend.read(SAVE_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as GameState;
    if (parsed.version !== 1 || !parsed.player || !Array.isArray(parsed.player.team)) {
      return null;
    }
    // Merge difensivo con lo stato iniziale: i salvataggi di versioni vecchie
    // con campi mancanti non rompono il gioco.
    const base = createInitialState();
    return {
      ...base,
      ...parsed,
      player: { ...base.player, ...parsed.player },
      phase: 'explore',
    };
  } catch {
    return null;
  }
}

export function deleteSave(backend: SaveBackend): void {
  backend.delete(SAVE_KEY);
}
